// --- Shared activity type mapping tables ---
// Used by both case and promise recent activity GraphQL hooks.

/**
 * Maps a GraphQL activityType string to a UI tag category.
 * The tag type is compatible with both CaseUnifiedActivity["tag"] and PromiseUnifiedActivity["tag"].
 */
export const ACTIVITY_TYPE_TO_TAG: Record<string, 'Note' | 'Communication' | 'Activity'> = {
  note: 'Note',
  email: 'Communication',
  phone: 'Communication',
  sms: 'Communication',
  communication: 'Communication',
  promise: 'Activity',
  status_change: 'Activity',
  payment: 'Activity',
  system: 'Activity',
  case: 'Activity',
  dispute: 'Activity',
};

/**
 * Maps a GraphQL activityType string to a specific UI activity type.
 * The type is compatible with both CaseUnifiedActivity["type"] and PromiseUnifiedActivity["type"].
 */
export const ACTIVITY_TYPE_TO_TYPE: Record<string, string> = {
  email: 'email',
  phone: 'call',
  sms: 'sms',
  communication: 'communication',
  note: 'note',
  promise: 'promise',
  payment: 'payment',
  status_change: 'status_change',
  system: 'system',
  case: 'case_note',
  dispute: 'dispute',
};

/**
 * Format a snake_case outcome code into a human-readable label
 * (e.g. `payment_promised` -> `Payment Promised`).
 */
export function formatOutcomeLabel(outcome: string): string {
  return outcome
    .split('_')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
}

/**
 * Parse the references of the invoices attached to an email from activity metadata JSON.
 */
export function parseActivityInvoiceReferences(metadataJson: string | null): string[] | undefined {
  if (!metadataJson) return undefined;
  try {
    const metadata = JSON.parse(metadataJson) as Record<string, unknown>;
    if (!Array.isArray(metadata.invoiceReferences)) return undefined;

    const references = metadata.invoiceReferences.filter((x): x is string => typeof x === 'string' && x.length > 0);
    return references.length > 0 ? references : undefined;
  } catch {
    return undefined;
  }
}

export interface ActivityEmailReference {
  emailJobId: number;
  emailChannel: string;
}

/** Email identity recorded against an activity, used to drill into the email detail. */
export function parseActivityEmailReference(metadataJson: string | null): ActivityEmailReference | undefined {
  if (!metadataJson) return undefined;
  try {
    const metadata = JSON.parse(metadataJson) as Record<string, unknown>;
    const { emailJobId, emailChannel } = metadata;
    if (typeof emailJobId !== 'number' || typeof emailChannel !== 'string' || emailChannel.length === 0) {
      return undefined;
    }

    return { emailJobId, emailChannel };
  } catch {
    return undefined;
  }
}

/**
 * Parse the canonical note id from activity metadata JSON.
 * Present on note activities, and is what makes a timeline note editable.
 */
export function parseActivityNoteId(metadataJson: string | null): number | undefined {
  if (!metadataJson) return undefined;
  try {
    const metadata = JSON.parse(metadataJson) as Record<string, unknown>;
    return typeof metadata.noteId === 'number' ? metadata.noteId : undefined;
  } catch {
    return undefined;
  }
}

/**
 * Parse the outcome field from activity metadata JSON.
 */
export function parseActivityOutcome(metadataJson: string | null): string | undefined {
  if (!metadataJson) return undefined;
  try {
    const metadata = JSON.parse(metadataJson) as Record<string, unknown>;
    if (typeof metadata.outcome === 'string' && metadata.outcome.length > 0) {
      return formatOutcomeLabel(metadata.outcome);
    }
  } catch {
    // ignore malformed metadata
  }
  return undefined;
}
